import { MoreHorizOutlined } from '@mui/icons-material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next';
import WorkflowSessionModal from '../../../WorkflowSessionModal';
import { encryptString, decryptString } from '../../../../../utils/utilFunctions';
import { authenticationWebServiceSourceTypeOptions, authTypeOptions } from '../../../../../constants/constantList';
import { api } from '../../../../../redux/api';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import CustomHeaderFieldsModal from '../CustomHeaderFieldsModal';
import { ParentScreenCtx } from '../../../../../pages/workbench_build/WorkbenchBuild';
import ErrorModal from '../../../Forms/FieldDataValidation/ErrorModal';
import SmartPasswordField from '../../../../../commonComponents/SmartPasswordField';

const ServiceDetailsTab = ({ disableControls, repoPDC, useConnection, submitServiceDetails, screen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, account } = useSelector((state) => state.queryParams);
  const [oldUrl, setOldUrl] = useState("");
  const [wsdlCombo, setWsdlCombo] = useState({});
  const parentScreen = useContext(ParentScreenCtx);
  const selectedNode = useSelector(
    (state) => state.treeData[parentScreen].rightTree.tree.body.selectedNode
  );
  const { leftTree } = useSelector((state) => state.treeData[parentScreen]);
  const sourceWorkflowName = leftTree.tree.body.selectedNode.workflowGroup;
  
  // Dynamic reference for INBOUND/OUTBOUND based on screen type
  const nodeData = selectedNode?.[screen === 'GET_DATA' ? 'INBOUND' : 'OUTBOUND'];

  const [bindingOptions, setBindingOptions] = useState([
    {
      text: t("EMPTY_COMBO"),
      value: t("EMPTY_COMBO"),
    },
  ]);
  const [operationOptions, setOperationOptions] = useState([
    {
      text: t("EMPTY_COMBO"),
      value: t("EMPTY_COMBO"),
    },
  ]);
  const [servicePortOptions, setServicePortOptions] = useState([
    {
      text: t("EMPTY_COMBO"),
      value: t("EMPTY_COMBO"),
    },
  ]);

  const [privateKeys, setPrivateKeys] = useState([]);
  const [keysFromApi, setKeysFromApi] = useState([]);
  const [useWSDL, setUseWSDL] = useState(false);
  const [wsdlFields, setWsdlFields] = useState({
    wsdlPath: "",
    binding: bindingOptions[0]?.value,
    operation: operationOptions[0]?.value,
    servicePort: servicePortOptions[0]?.value,
  });
  const [serviceEndpointUrl, setServiceEndpointUrl] = useState("");
  const [targetNameSpace, setTargetNameSpace] = useState("");
  const [requestElement, setRequestElement] = useState("");
  const [sslVersionOptions, setSslVersionOptions] = useState([]);
  const [soapAction, setSoapAction] = useState("");
  const [soapPrefix, setSoapPrefix] = useState("");
  const [authentication, setAuthentication] = useState("");
  const [authUsername, setAuthUsername] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [realm, setRealm] = useState("");
  const [timeout, setTimeout] = useState("");
  const [privateKeyAlias, setPrivateKeyAlias] = useState("");
  const [sslVersion, setSslVersion] = useState(t("SELECT") || "");
  const [useSoap, setUseSoap] = useState(false);
  const [maintainHttpSession, setMaintainHttpSession] = useState(false);
  const [recallRefresh, setRecallRefresh] = useState(false);
  // WS_SECURITY specific fields
  const [policy, setPolicy] = useState("");
  const [keystore, setKeystore] = useState("");
  const [keystorePassword, setKeystorePassword] = useState("");
  const [keystoreType, setKeystoreType] = useState("");
  const [privateKeyPassword, setPrivateKeyPassword] = useState("");
  const [userCertAlias, setUserCertAlias] = useState("");
  const [userCertOptions, setUserCertOptions] = useState([]);
  
  // Additional state for SEND_DATA checkboxes
  const [receiveSyncResponse, setReceiveSyncResponse] = useState(false);
  const [sendSourceAsSoapBody, setSendSourceAsSoapBody] = useState(false);
  const [disableChunkedData, setDisableChunkedData] = useState(false);

  const [valueLabel, setValueLabel] = useState("");

  const [workflowSessionModal, setWorkflowSessionModal] = useState({
    open: false,
    isFirstTime: false,
  });
  const [openCustomHeaderModal, setOpenCustomHeaderModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const valuesHandler = (value) => {
    if (workflowMappings[valueLabel]) {
      const { stateSetter, key } = workflowMappings[valueLabel];
      let encryptedValue = value;
      if (key === "KEYSTORE_PWD" || key === "PRIVATE_KEY_PWD") {
        encryptedValue = encryptString(value, process.env.REACT_APP_SECRET_KEY2);
      }
      stateSetter(value);
      debounceSubmit({ [key]: encryptedValue });
    }
  };

  const workflowMappings = {
    [t("LBL_WS_SERVICE_ENDPOINT_URL")]: {
      stateSetter: setServiceEndpointUrl,
      key: "CONNECTIONSTRING",
      value: serviceEndpointUrl,
    },
    [t("LBL_WS_TARGET_NAMESPACE")]: {
      stateSetter: setTargetNameSpace,
      key: "TARGET_NAMESPACE",
      value: targetNameSpace,
    },
    [t("LBL_WS_REQUEST_ELEMENT")]: {
      stateSetter: setRequestElement,
      key: "REQUEST_ELEMENT_NAME",
      value: requestElement,
    },
    [t("LBL_WS_SOAP_ACTION")]: {
      stateSetter: setSoapAction,
      key: "SOAP_ACTION",
      value: soapAction,
    },
    [t("LBL_TIMEOUT")]: {
      stateSetter: setTimeout,
      key: "TIMEOUT",
      value: timeout,
    },
    [t("LBL_KEY_ALIAS")]: {
      stateSetter: setPrivateKeyAlias,
      key: "KEY_ALIAS",
      value: privateKeyAlias,
    },
    [t("LBL_WS_USERNAME")]: {
      stateSetter: setAuthUsername,
      key: "USERNAME",
      value: authUsername,
    },
    [t("LBL_REALM")]: {
      stateSetter: setRealm,
      key: "REALM",
      value: realm,
    },
    [t("LBL_POLICY")]: {
      stateSetter: setPolicy,
      key: "POLICY",
      value: policy,
    },
    [t("LBL_KEYSTORE")]: {
      stateSetter: setKeystore,
      key: "KEYSTORE",
      value: keystore,
    },
    [t("LBL_KEYSTORE_PASSWORD")]: {
      stateSetter: setKeystorePassword,
      key: "KEYSTORE_PWD",
      value: keystorePassword,
    },
    [t("LBL_KEYSTORE_TYPE")]: {
      stateSetter: setKeystoreType,
      key: "KEYSTORE_TYPE",
      value: keystoreType,
    },
    [t("LBL_PRIVATE_KEY_PASSWORD")]: {
      stateSetter: setPrivateKeyPassword,
      key: "PRIVATE_KEY_PWD",
      value: privateKeyPassword,
    },
    [t("LBL_USER_CERT_ALIAS")]: {
      stateSetter: setUserCertAlias,
      key: "USER_CERT_ALIAS",
      value: userCertAlias,
    },
  };

  function handleOperationChange(e) {
    const operation = e.target.value;

    setWsdlFields({ ...wsdlFields, operation: e.target.value });

    const updateObj = { add: { [e.target.name]: e.target.value } };
    if (operation === t("EMPTY_COMBO")) {
      updateObj.add["REQUEST_ELEMENT_NAME"] = null;
      updateObj.add["SOAP_ACTION"] = null;

      setRequestElement("");
      setSoapAction("");
      updateObj.add["ARGS"] = null;
    } else {
      const operationData = wsdlCombo?.soapBindings?.bindings
        ?.find((ele) => ele.bindingName === wsdlFields?.binding)
        ?.operations?.find((ele) => ele.operationName === operation);
      updateObj.add["REQUEST_ELEMENT_NAME"] =
        operationData?.fields?.request_element_name;
      updateObj.add["SOAP_ACTION"] = operationData?.fields?.soapAction;
      setRequestElement(operationData?.fields?.request_element_name);
      setSoapAction(operationData?.fields?.soapAction);

      updateObj.add["ARGS"] =
        [
          ...operationData?.fields?.outputParameters.map((ele) => ({
            ARGNAME: ele,
            ARGTYPE: "OUT",
            WRITE_TO_RESPONSE: "TRUE",
          })),
          ...operationData?.fields?.inputParameters.map((ele) => ({
            ARGNAME: ele,
            ARGTYPE: "IN",
            FIELDTYPE: "ALPHA",
          })),
        ] || [];
    }
    debounceSubmit(updateObj);
  }

  function handleServicePort(e) {
    const servicePort = e.target.value;
    setWsdlFields({ ...wsdlFields, servicePort: e.target.value });

    const updateObj = { add: { [e.target.name]: e.target.value } };
    if (servicePort === t("EMPTY_COMBO")) {
      updateObj.add["CONNECTIONSTRING"] = null;
      setServiceEndpointUrl("");
    } else {
      const serviceData = wsdlCombo?.soapBindings?.bindings
        ?.find((ele) => ele.bindingName === wsdlFields?.binding)
        ?.servicePorts?.find((ele) => ele.name === servicePort);
      updateObj.add["CONNECTIONSTRING"] = serviceData?.service_endpoint;
      setServiceEndpointUrl(serviceData?.service_endpoint);
    }
    debounceSubmit(updateObj);
  }

  function getWsDlCombos(url, cache, oldUrl, refresh = false) {
    setRecallRefresh(refresh);
    const sendCache = url === oldUrl ? cache : "";
    dispatch(
      api.endpoints.getWsdlCombos.initiate(
        {
          user,
          account,
          fileUrl: encodeURIComponent(url),
          wsdl_cache: encodeURIComponent(sendCache),
          old_path: encodeURIComponent(oldUrl),
          refresh,
        },
        { forceRefetch: true }
      )
    )
      .then((res) => {
        if (res?.data?.data?.hasOwnProperty("error") || res?.data?.current_state === "ERROR") {
          setWsdlFields((prev) => ({
            ...prev,
            wsdlPath: oldUrl,
          }));
          debounceSubmit({ WSDL_PATH: oldUrl });
          setShowErrorModal(true);
          setErrorMessage(url);
        } else {
          setWsdlCombo(res?.data?.data);
          setOldUrl(url);
        }
      })
      .catch(() => {
        setWsdlFields((prev) => ({
          ...prev,
          wsdlPath: oldUrl,
        }));
        debounceSubmit({ WSDL_PATH: oldUrl });
      });
  }

  useEffect(() => {
    if (selectedNode) {
      setOldUrl(nodeData?.WSDL_PATH || "");
      const cache = nodeData?.WSDL_CACHED || "";
      if (nodeData?.WSDL_PATH)
        getWsDlCombos(nodeData?.WSDL_PATH, cache, nodeData?.WSDL_PATH);

      setUseWSDL(nodeData?.USE_WSDL === "TRUE" ? true : false);
      setWsdlFields({
        wsdlPath: nodeData?.WSDL_PATH || "",
        binding: nodeData?.WSDL_BINDING || bindingOptions[0].value,
        operation: nodeData?.WSDL_BINDING_OPERATION || operationOptions[0].value,
        servicePort: nodeData?.WSDL_BINDING_SERVICE || servicePortOptions[0].value,
      });
      setServiceEndpointUrl(nodeData?.CONNECTIONSTRING || "");
      setTargetNameSpace(nodeData?.TARGET_NAMESPACE || "");
      setRequestElement(nodeData?.REQUEST_ELEMENT_NAME || "");
      setSoapAction(nodeData?.SOAP_ACTION || "");
      setSoapPrefix(nodeData?.SOAP_PREFIX || "tns");
      setAuthentication(nodeData?.AUTHENTICATE || authTypeOptions[0].value);
      setAuthUsername(nodeData?.USERNAME || "");
      setAuthPassword(nodeData?.PASSWORD || "");
      setRealm(nodeData?.REALM || "");
      setTimeout(nodeData?.TIMEOUT || "");
      setPrivateKeyAlias(nodeData?.KEY_ALIAS || "");
      setSslVersion(nodeData?.SSL_TYPE || "");
      setUseSoap(nodeData?.WRITE_SOAP_BODY === "TRUE" || false);
      setMaintainHttpSession(nodeData?.MAINTAIN_SESSION === "TRUE" || false);
      
      // Initialize SEND_DATA specific checkboxes
      if (screen === 'SEND_DATA') {
        setReceiveSyncResponse(nodeData?.SYNCHRONOUS === "TRUE" || false);
        setSendSourceAsSoapBody(nodeData?.SEND_SOURCE_AS_SOAP_BODY === "TRUE" || false);
        setDisableChunkedData(nodeData?.DISABLE_CHUNKED_DATA === "TRUE" || false);
      }
      
      // Initialize WS_SECURITY specific fields
      setPolicy(nodeData?.POLICY || "");
      setKeystore(nodeData?.KEYSTORE || "");
      setKeystorePassword(nodeData?.KEYSTORE_PWD ? decryptString(nodeData?.KEYSTORE_PWD, process.env.REACT_APP_SECRET_KEY2) : "");
      setKeystoreType(nodeData?.KEYSTORE_TYPE || "");
      setPrivateKeyPassword(nodeData?.PRIVATE_KEY_PWD ? decryptString(nodeData?.PRIVATE_KEY_PWD, process.env.REACT_APP_SECRET_KEY2) : "");
      setUserCertAlias(nodeData?.USER_CERT_ALIAS || "");
    }
  }, [selectedNode?.key]);

  useEffect(() => {
    dispatch(api.endpoints.getSslVersion.initiate({ account })).then((res) => {
      if (res?.data?.current_state === "SUCCESS") {
        setSslVersionOptions(res?.data?.data);
      }
    });
    dispatch(api.endpoints.getPrivateKeys.initiate({private_key: true}, { forceRefetch: true })).then((res) => {
      if (res?.data?.current_state === 'SUCCESS') {
          setPrivateKeys(res?.data?.data);
          setUserCertOptions(res?.data?.data);
      } else {
        setPrivateKeys([]);
        setUserCertOptions([]);
      }
  })
  }, [account, dispatch]);
  
  const debounceDispatch = useCallback(
    debounce((params) => {
      dispatch(api.endpoints.getWsSecurityKeyAliases.initiate(params, { forceRefetch: true }))
      .then((res) => {
        if(res?.data?.current_state === 'SUCCESS'){
          setKeysFromApi(res?.data?.data);
        } else {
          setKeysFromApi([]);
        }
      })
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    if(keystore && keystoreType){
      if((keystorePassword === privateKeyPassword) && keystorePassword !== ""){
        debounceDispatch({keystore: encodeURIComponent(keystore), keystorePassword, keystoreType, account, workflowGroup: sourceWorkflowName, user})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keystore, keystorePassword, privateKeyPassword, keystoreType]);

  useEffect(() => {
    if (Object.keys(wsdlCombo).length > 0) {
      setTargetNameSpace(wsdlCombo?.schemaInfo?.targetNamespace || "");
      const addObj = {
        add: {},
      };

      if (wsdlCombo?.schemaInfo?.targetNamespace)
        addObj.add["TARGET_NAMESPACE"] =
          wsdlCombo?.schemaInfo?.targetNamespace || "";
      if (wsdlCombo?.WSDL_CACHED)
        addObj.add["WSDL_CACHED"] = wsdlCombo?.WSDL_CACHED || "";
      if (wsdlCombo?.WSDL_POLICY_CACHED)
        addObj.add["WSDL_POLICY_CACHED"] = wsdlCombo?.WSDL_POLICY_CACHED || "";

      if(recallRefresh){
        addObj.add["ARGS"] = null;
        addObj.add["WSDL_BINDING"] = t("SELECT");
        addObj.add["WSDL_BINDING_OPERATION"] = t("EMPTY_COMBO");
        addObj.add["WSDL_BINDING_SERVICE"] = t("EMPTY_COMBO");
        addObj.add["CONNECTIONSTRING"] = null;
        addObj.add["REQUEST_ELEMENT_NAME"] = null;
        addObj.add["SOAP_ACTION"] = null;
        setServiceEndpointUrl("");
        setRequestElement("");
        setSoapAction("");
        setRecallRefresh(false);
      }

      Object.keys(addObj?.add).length && debounceSubmit(addObj);
      setBindingOptions(
        wsdlCombo?.soapBindings?.bindings?.length
          ? [
              { text: t("SELECT"), value: t("SELECT") },
              ...wsdlCombo?.soapBindings?.bindings?.map((item) => ({
                text: item?.bindingName || "",
                value: item?.bindingName || "",
              })),
            ]
          : [{ text: t("EMPTY_COMBO"), value: t("EMPTY_COMBO") }]
      );
      setWsdlFields((prev) => ({
        ...prev,
        binding: recallRefresh ? t("SELECT") : (nodeData?.WSDL_BINDING || bindingOptions[0].value),
        operation: recallRefresh ? t("EMPTY_COMBO") : (nodeData?.WSDL_BINDING_OPERATION || operationOptions[0].value),
        servicePort: recallRefresh ? t("EMPTY_COMBO") : (nodeData?.WSDL_BINDING_SERVICE || servicePortOptions[0].value),
      }));
    }
  }, [wsdlCombo]);

  useEffect(() => {
    if (wsdlFields?.binding) {
      const selectedBinding =
        wsdlCombo?.soapBindings?.bindings?.find(
          (item) => item?.bindingName === wsdlFields?.binding
        ) || {};
      const operations = selectedBinding?.operations || [];
      const selectedSerPort = selectedBinding?.servicePorts || [];

      setOperationOptions([
        { 
          text: operations.length ? t("SELECT") : t("EMPTY_COMBO"), 
          value: operations.length ? t("SELECT") : t("EMPTY_COMBO") 
        },
        ...operations?.map((item) => ({
          text: item?.operationName || "",
          value: item?.operationName || "",
        })),
      ]);

      setServicePortOptions([
        { 
          text: selectedSerPort.length ? t("SELECT") : t("EMPTY_COMBO"), 
          value: selectedSerPort.length ? t("SELECT") : t("EMPTY_COMBO") 
        },
        ...selectedSerPort?.map((item) => ({
          text: item?.name || "",
          value: item?.name || "",
        })),
      ]);
    }
  }, [wsdlFields?.binding, wsdlCombo?.soapBindings?.bindings, t]);

  const debounceSubmit = debounce(submitServiceDetails, 400);

  // Special handlers for password fields that need encryption
  const handleKeystorePasswordChange = (value) => {
    setKeystorePassword(value);
    const encryptedValue = value ? encryptString(value, process.env.REACT_APP_SECRET_KEY2) : null;
    debounceSubmit({ KEYSTORE_PWD: encryptedValue });
  };

  const handlePrivateKeyPasswordChange = (value) => {
    setPrivateKeyPassword(value);
    const encryptedValue = value ? encryptString(value, process.env.REACT_APP_SECRET_KEY2) : null;
    debounceSubmit({ PRIVATE_KEY_PWD: encryptedValue });
  };

  return (
    <div style={styles.serviceContainer}>
      <Box
        className="flex align-center"
        style={{ justifyContent: "space-between", width: "100%" }}
      >
        <Box className="flex align-center">
          <Checkbox
            checked={useWSDL || false}
            name="USE_WSDL"
            disabled={disableControls}
            onChange={(e) => {
              setUseWSDL(e.target.checked);

              if (e.target.checked)
                debounceSubmit({
                  [e.target.name]: e.target.checked ? "TRUE" : "FALSE",
                });
              else {
                setWsdlFields({
                  wsdlPath: "",
                  binding: t("EMPTY_COMBO"),
                  operation: t("EMPTY_COMBO"),
                  servicePort: t("EMPTY_COMBO"),
                });

                setServiceEndpointUrl("");
                setTargetNameSpace("");
                setRequestElement("");
                setSoapAction("");
                setBindingOptions([{ text: t("EMPTY_COMBO"), value: t("EMPTY_COMBO") }]);
                setOperationOptions([{ text: t("EMPTY_COMBO"), value: t("EMPTY_COMBO") }]);
                setServicePortOptions([{ text: t("EMPTY_COMBO"), value: t("EMPTY_COMBO") }]);

                debounceSubmit({
                  add: {
                    [e.target.name]: e.target.checked ? "FALSE" : "TRUE",
                    USE_WSDL: null,
                    WSDL_PATH: null,
                    WSDL_BINDING: null,
                    WSDL_BINDING_OPERATION: null,
                    WSDL_BINDING_SERVICE: null,
                    CONNECTIONSTRING: null,
                    TARGET_NAMESPACE: null,
                    REQUEST_ELEMENT_NAME: null,
                    SOAP_ACTION: null,
                    ARGS: null,
                  },
                });
              }
            }}
          />
          <Typography>{t("BTN_WS_USE_WSDL")}</Typography>
        </Box>
        <Button
          variant="outlined"
          disabled={disableControls || !useWSDL}
          startIcon={<RefreshOutlinedIcon />}
          onClick={() =>
            getWsDlCombos(wsdlFields?.wsdlPath || "", "", oldUrl, true)
          }
        >
          {" "}
          {t("REFRESH")}{" "}
        </Button>
      </Box>

      <TextField
        label={t("LBL_WS_WSDL_PATH")}
        variant="outlined"
        className="width-100"
        size="small"
        value={wsdlFields.wsdlPath}
        name="WSDL_PATH"
        sx={{
          input: {
            fontWeight:
              repoPDC?.WSDL_PATH !== wsdlFields.wsdlPath &&
              useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
        onChange={(e) => {
          setWsdlFields({ ...wsdlFields, wsdlPath: e.target.value });
          debounceSubmit({ [e.target.name]: e.target.value || null });
        }}
        disabled={disableControls || !useWSDL || false}
      />

      <TextField
        label={t("LBL_WS_BINDING")}
        select
        variant="outlined"
        className="width-100"
        size="small"
        disabled={disableControls || !useWSDL || false}
        name="WSDL_BINDING"
        value={bindingOptions.map((item) => item.value).includes(wsdlFields.binding) ? wsdlFields.binding : bindingOptions[0]?.value}
        onChange={(e) => {
          if (wsdlFields?.binding !== e.target.value) {
            setWsdlFields({
              ...wsdlFields,
              binding: e.target.value,
              operation: t("EMPTY_COMBO"),
              servicePort: t("EMPTY_COMBO"),
            });
            debounceSubmit({
              add: {
                [e.target.name]: e.target.value,
                WSDL_BINDING_OPERATION: t("EMPTY_COMBO"),
                WSDL_BINDING_SERVICE: t("EMPTY_COMBO"),
              },
            });
          }
        }}
        sx={{
          input: {
            fontWeight:
              repoPDC?.WSDL_BINDING !== wsdlFields.binding &&
              useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
      >
        {bindingOptions.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.text}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label={t("LBL_WS_BINDING_OPERATION")}
        select
        variant="outlined"
        className="width-100"
        size="small"
        disabled={disableControls || !useWSDL || false}
        name="WSDL_BINDING_OPERATION"
        value={operationOptions.map((item) => item.value).includes(wsdlFields.operation) ? wsdlFields.operation : operationOptions[0]?.value}
        onChange={handleOperationChange}
        sx={{
          input: {
            fontWeight:
              repoPDC?.WSDL_BINDING_OPERATION !== wsdlFields.operation &&
              useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
      >
        {operationOptions.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.text}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label={t("LBL_WS_BINDING_SERVICE")}
        select
        variant="outlined"
        className="width-100"
        size="small"
        disabled={disableControls || !useWSDL || false}
        name="WSDL_BINDING_SERVICE"
        value={servicePortOptions.map((item) => item.value).includes(wsdlFields.servicePort) ? wsdlFields.servicePort : servicePortOptions[0]?.value}
        onChange={handleServicePort}
        sx={{
          input: {
            fontWeight:
              repoPDC?.WSDL_BINDING_SERVICE !== wsdlFields.servicePort &&
              useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
      >
        {servicePortOptions.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.text}
          </MenuItem>
        ))}
      </TextField>

      <InputFieldWithWfModal
        setWorkflowSessionModal={setWorkflowSessionModal}
        debounceSubmit={debounceSubmit}
        setValueLabel={setValueLabel}
        label={t("LBL_WS_SERVICE_ENDPOINT_URL")}
        type="text"
        name="CONNECTIONSTRING"
        value={serviceEndpointUrl || ""}
        disabled={disableControls}
        onChangeHandler={setServiceEndpointUrl}
        repoPDC={repoPDC}
        useConnection={useConnection}
      />

      <InputFieldWithWfModal
        setWorkflowSessionModal={setWorkflowSessionModal}
        debounceSubmit={debounceSubmit}
        setValueLabel={setValueLabel}
        label={"LBL_WS_TARGET_NAMESPACE"}
        type="text"
        name="TARGET_NAMESPACE"
        value={targetNameSpace || ""}
        disabled={disableControls}
        onChangeHandler={setTargetNameSpace}
        repoPDC={repoPDC}
        useConnection={useConnection}
      />

      <InputFieldWithWfModal
        setWorkflowSessionModal={setWorkflowSessionModal}
        debounceSubmit={debounceSubmit}
        setValueLabel={setValueLabel}
        label={t("LBL_WS_REQUEST_ELEMENT")}
        type="text"
        name="REQUEST_ELEMENT_NAME"
        value={requestElement || ""}
        disabled={disableControls}
        onChangeHandler={setRequestElement}
        repoPDC={repoPDC}
        useConnection={useConnection}
      />

      <InputFieldWithWfModal
        setWorkflowSessionModal={setWorkflowSessionModal}
        debounceSubmit={debounceSubmit}
        setValueLabel={setValueLabel}
        label={t("LBL_WS_SOAP_ACTION")}
        type="text"
        name="SOAP_ACTION"
        value={soapAction || ""}
        disabled={disableControls}
        onChangeHandler={setSoapAction}
        repoPDC={repoPDC}
        useConnection={useConnection}
      />

      <TextField
        label={t("LBL_WS_SOAP_PREFIX")}
        variant="outlined"
        className="width-100"
        size="small"
        name="SOAP_PREFIX"
        value={soapPrefix}
        disabled={disableControls}
        onChange={(e) => {
          setSoapPrefix(e.target.value);
          debounceSubmit({ [e.target.name]: e.target.value || null });
        }}
        sx={{
          input: {
            fontWeight:
              ((repoPDC?.SOAP_PREFIX !== soapPrefix && repoPDC?.SOAP_PREFIX) ||
                (!repoPDC?.SOAP_PREFIX && soapPrefix !== "tns")) &&
              useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
      />

      <TextField
        label={t("LBL_AUTHENTICATION")}
        select
        variant="outlined"
        className="width-100"
        size="small"
        name="AUTHENTICATE"
        value={
          authentication || authenticationWebServiceSourceTypeOptions[0]?.value
        }
        disabled={disableControls}
        onChange={(e) => {
          setAuthentication(e.target.value);
          debounceSubmit({ [e.target.name]: e.target.value });
        }}
        sx={{
          input: {
            fontWeight:
              repoPDC?.AUTHENTICATE !== authentication &&
              useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
      >
        {authenticationWebServiceSourceTypeOptions.slice(0, screen === "SEND_DATA" ? 4 : 3).map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.text}
          </MenuItem>
        ))}
      </TextField>

      {["BASIC", "KERBEROS", "WS_SECURITY"].includes(authentication) && (
        <>
                      <InputFieldWithWfModal
              setWorkflowSessionModal={setWorkflowSessionModal}
              debounceSubmit={debounceSubmit}
              setValueLabel={setValueLabel}
              label="LBL_WS_USERNAME"
              type="text"
              name="USERNAME"
              value={authUsername || ""}
              disabled={disableControls}
              onChangeHandler={setAuthUsername}
              repoPDC={repoPDC}
              useConnection={useConnection}
            />

                      <SmartPasswordField
              label={t("LBL_WS_PASSWORD")}
              variant="outlined"
              className="width-100"
              size="small"
              type="password"
              name="PASSWORD"
              value={authPassword || ""}
              disabled={disableControls}
              onChange={(e) => {
                setAuthPassword(e.target.value);
                debounceSubmit({ [e.target.name]: e.target.value || null });
              }}
              sx={{
                input: {
                  fontWeight:
                    repoPDC?.PASSWORD !== authPassword &&
                    useConnection === "preDefined"
                      ? "bold"
                      : "normal",
                },
              }}
            />
        </>
      )}

          {authentication === "KERBEROS" && (
          <InputFieldWithWfModal
            setWorkflowSessionModal={setWorkflowSessionModal}
            debounceSubmit={debounceSubmit}
            setValueLabel={setValueLabel}
            label="LBL_REALM"
            type="text"
            name="REALM"
            value={realm || ""}
            disabled={disableControls}
            onChangeHandler={setRealm}
            repoPDC={repoPDC}
            useConnection={useConnection}
          />
        )}

        {authentication === "WS_SECURITY" && (
          <>
            <InputFieldWithWfModal
              setWorkflowSessionModal={setWorkflowSessionModal}
              debounceSubmit={debounceSubmit}
              setValueLabel={setValueLabel}
              label="LBL_POLICY"
              type="text"
              name="POLICY"
              value={policy || ""}
              disabled={disableControls}
              onChangeHandler={setPolicy}
              repoPDC={repoPDC}
              useConnection={useConnection}
            />

            <InputFieldWithWfModal
              setWorkflowSessionModal={setWorkflowSessionModal}
              debounceSubmit={debounceSubmit}
              setValueLabel={setValueLabel}
              label="LBL_KEYSTORE"
              type="text"
              name="KEYSTORE"
              value={keystore || ""}
              disabled={disableControls}
              onChangeHandler={setKeystore}
              repoPDC={repoPDC}
              useConnection={useConnection}
            />

            <InputFieldWithWfModal
              setWorkflowSessionModal={setWorkflowSessionModal}
              debounceSubmit={debounceSubmit}
              setValueLabel={setValueLabel}
              label="LBL_KEYSTORE_PASSWORD"
              type="password"
              name="KEYSTORE_PWD"
              value={keystorePassword || ""}
              disabled={disableControls}
              onChangeHandler={handleKeystorePasswordChange}
              repoPDC={repoPDC}
              useConnection={useConnection}
              skipSubmit={true}
            />

            <InputFieldWithWfModal
              setWorkflowSessionModal={setWorkflowSessionModal}
              debounceSubmit={debounceSubmit}
              setValueLabel={setValueLabel}
              label="LBL_KEYSTORE_TYPE"
              type="text"
              name="KEYSTORE_TYPE"
              value={keystoreType || ""}
              disabled={disableControls}
              onChangeHandler={setKeystoreType}
              repoPDC={repoPDC}
              useConnection={useConnection}
            />

            <InputFieldWithWfModal
              setWorkflowSessionModal={setWorkflowSessionModal}
              debounceSubmit={debounceSubmit}
              setValueLabel={setValueLabel}
              label="LBL_PRIVATE_KEY_PASSWORD"
              type="password"
              name="PRIVATE_KEY_PWD"
              value={privateKeyPassword || ""}
              disabled={disableControls}
              onChangeHandler={handlePrivateKeyPasswordChange}
              repoPDC={repoPDC}
              useConnection={useConnection}
              skipSubmit={true}
            />

            <div style={{ display: "flex", gap: "4px", width: "100%" }}>
              <FormControl className="width-100" size="small">
                <Autocomplete
                  freeSolo
                  options={keysFromApi.length > 0 ? keysFromApi : userCertOptions}
                  name="USER_CERT_ALIAS"
                  value={userCertAlias || ""}
                  disabled={disableControls}
                  onChange={(_, newValue) => {
                    setUserCertAlias(newValue || "");
                    debounceSubmit({ USER_CERT_ALIAS: newValue || null });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("LBL_USER_CERT_ALIAS")}
                      variant="outlined"
                      onChange={(e) => {
                        setUserCertAlias(e.target.value);
                        debounceSubmit({ USER_CERT_ALIAS: e.target.value || null });
                      }}
                      size="small"
                    />
                  )}
                  fullWidth
                  sx={{
                    input: {
                      fontWeight:
                        repoPDC?.USER_CERT_ALIAS !== userCertAlias &&
                        useConnection === "preDefined"
                          ? "bold"
                          : "normal",
                    },
                  }}
                />
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                disabled={disableControls}
                onClick={() => {
                  setValueLabel(t("LBL_USER_CERT_ALIAS"));
                  setWorkflowSessionModal({ open: true, isFirstTime: true });
                }}
                sx={{ width: "40px", minWidth: "40px" }}
              >
                <MoreHorizOutlined fontSize="small" />
              </Button>
            </div>
          </>
        )}

      <InputFieldWithWfModal
        setWorkflowSessionModal={setWorkflowSessionModal}
        debounceSubmit={debounceSubmit}
        setValueLabel={setValueLabel}
        label={"LBL_TIMEOUT"}
        type="number"
        value={timeout || ""}
        disabled={disableControls}
        name="TIMEOUT"
        onChangeHandler={(val) => setTimeout(val)}
        repoPDC={repoPDC}
        useConnection={useConnection}
      />

      <div style={{ display: "flex", gap: "4px", width: "100%" }}>
        <FormControl className="width-100" size="small">
          <Autocomplete
            freeSolo
            options={keysFromApi.length > 0 ? keysFromApi : privateKeys}
            name="KEY_ALIAS"
            value={privateKeyAlias || ""}
            disabled={disableControls}
            onChange={(_, newValue) => {
              setPrivateKeyAlias(newValue || "");
              debounceSubmit({ KEY_ALIAS: newValue || null });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("LBL_KEY_ALIAS")}
                variant="outlined"
                onChange={(e) => {
                  setPrivateKeyAlias(e.target.value);
                  debounceSubmit({ KEY_ALIAS: e.target.value || null });
                }}
                size="small"
              />
            )}
            fullWidth
            sx={{
              input: {
                fontWeight:
                  repoPDC?.KEY_ALIAS !== privateKeyAlias &&
                  useConnection === "preDefined"
                    ? "bold"
                    : "normal",
              },
            }}
          />
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          disabled={disableControls}
          onClick={() => {
            setValueLabel(t("LBL_KEY_ALIAS"));
            setWorkflowSessionModal({ open: true, isFirstTime: true });
          }}
          sx={{ width: "40px", minWidth: "40px" }}
        >
          <MoreHorizOutlined fontSize="small" />
        </Button>
      </div>

      <TextField
        label={t("SSL_TYPE")}
        select
        variant="outlined"
        className="width-100"
        size="small"
        name="SSL_TYPE"
        disabled={disableControls}
        value={sslVersion || sslVersionOptions[0]}
        onChange={(e) => {
          setSslVersion(e.target.value);
          debounceSubmit({ [e.target.name]: e.target.value });
        }}
        sx={{
          input: {
            fontWeight:
              repoPDC?.SSL_TYPE !== sslVersion && useConnection === "preDefined"
                ? "bold"
                : "normal",
          },
        }}
      >
        {sslVersionOptions.map((option, index) => (
          <MenuItem key={index} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="outlined"
        className="width-100"
        onClick={() => setOpenCustomHeaderModal(true)}
      >
        {t("LBL_HTTP_CUSTOM_HEADER_FIELDS")}
      </Button>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <FormControlLabel
          label={t("BTN_WS_SOAP_BODY_AS_RESULT")}
          sx={{ height: "30px" }}
          className={"width-fit-content"}
          control={
            <Checkbox
              checked={useSoap || false}
              size="small"
              disabled={disableControls}
              name="WRITE_SOAP_BODY"
              onChange={(e) => {
                setUseSoap(e.target.checked);
                debounceSubmit({
                  [e.target.name]: e.target.checked ? "TRUE" : "FALSE",
                });
              }}
            />
          }
        />

        <FormControlLabel
          label={t("MAINTAIN_SESSION")}
          sx={{ height: "30px" }}
          className={"width-fit-content"}
          control={
            <Checkbox
              checked={maintainHttpSession || false}
              size="small"
              disabled={disableControls}
              name="MAINTAIN_SESSION"
              onChange={(e) => {
                setMaintainHttpSession(e.target.checked);
                debounceSubmit({
                  [e.target.name]: e.target.checked ? "TRUE" : "FALSE",
                });
              }}
            />
          }
        />

        {/* Additional checkboxes for SEND_DATA only */}
        {screen === 'SEND_DATA' && (
          <>
            <FormControlLabel
              label={t("BTN_RECEIVE_SYNC_RESPONSE")}
              sx={{ height: "30px" }}
              className={"width-fit-content"}
              control={
                <Checkbox
                  checked={receiveSyncResponse || false}
                  size="small"
                  disabled={disableControls || false}
                  name="SYNCHRONOUS"
                  onChange={(e) => {
                    setReceiveSyncResponse(e.target.checked);
                    debounceSubmit({
                      [e.target.name]: e.target.checked ? "TRUE" : "FALSE",
                    });
                  }}
                />
              }
            />

            <FormControlLabel
              label={t("BTN_WS_SOAP_BODY_SEND_SOURCE")}
              sx={{ height: "30px" }}
              className={"width-fit-content"}
              control={
                <Checkbox
                  checked={sendSourceAsSoapBody || false}
                  size="small"
                  disabled={disableControls || false}
                  name="SEND_SOURCE_AS_SOAP_BODY"
                  onChange={(e) => {
                    setSendSourceAsSoapBody(e.target.checked);
                    debounceSubmit({
                      [e.target.name]: e.target.checked ? "TRUE" : "FALSE",
                    });
                  }}
                />
              }
            />

            <FormControlLabel
              label={t("BTN_WS_DISABLE_CHUNKED_DATA")}
              sx={{ height: "30px" }}
              className={"width-fit-content"}
              control={
                <Checkbox
                  checked={disableChunkedData || false}
                  size="small"
                  disabled={disableControls || false}
                  name="DISABLE_CHUNKED_DATA"
                  onChange={(e) => {
                    setDisableChunkedData(e.target.checked);
                    debounceSubmit({
                      [e.target.name]: e.target.checked ? "TRUE" : "FALSE",
                    });
                  }}
                />
              }
            />
          </>
        )}
      </div>

      <WorkflowSessionModal
        valueLabel={valueLabel}
        showAccountData={true}
        showCallingWorkflow={true}
        showSystemData={true}
        showWorkflowSession={true}
        showDateAndTime={true}
        openModal={workflowSessionModal}
        setModalOpen={setWorkflowSessionModal}
        handleClose={() =>
          setWorkflowSessionModal({ open: false, isFirstTime: false })
        }
        initialValue={workflowMappings[valueLabel]?.value}
        valuesHandler={(value) => {
          valuesHandler(value);
        }}
        inputType={valueLabel === t("LBL_KEYSTORE_PASSWORD") || valueLabel === t("LBL_PRIVATE_KEY_PASSWORD") ? "password" : "text"}
        specialPasswordField={valueLabel === t("LBL_KEYSTORE_PASSWORD") || valueLabel === t("LBL_PRIVATE_KEY_PASSWORD")}
      />
      <CustomHeaderFieldsModal
        openModal={openCustomHeaderModal}
        handleCancelClick={() => setOpenCustomHeaderModal(false)}
        handleChange={submitServiceDetails}
      ></CustomHeaderFieldsModal>

      {showErrorModal && (
        <ErrorModal
          errorModal={showErrorModal}
          setErrorModal={setShowErrorModal}
          errorMessage={`${t("BOX_WSDL_PROCESS_ERROR")}: ${errorMessage}`}
          errorType={t("ERROR")}
          OkHandler={() => {
            setShowErrorModal(false);
            setErrorMessage("");
          }}
        />
      )}
    </div>
  );
}

export default ServiceDetailsTab;

export const styles = {
  serviceContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    justifyContent: "center",
    padding: "10px"
  }
}


const InputFieldWithWfModal = React.memo(({ label, type = "text", disabled, name, value, onChangeHandler, setWorkflowSessionModal, debounceSubmit, setValueLabel, repoPDC, useConnection, skipSubmit = false }) => {
  const { t } = useTranslation();
  const FieldComponent = type === "password" ? SmartPasswordField : TextField;
  
  return <div style={{ display: "flex", gap: "4px", width: "100%" }}>
    <FormControl className="width-100" size="small">
      <FieldComponent
        variant="outlined"
        size="small"
        label={t(label)}
        type={type}
        value={value || ""}
        disabled={disabled}
        onChange={(e) => {
          onChangeHandler(e.target.value);
          if (!skipSubmit) {
            debounceSubmit({ [name]: e.target.value || null });
          }
        }}
        sx={{ input: { 
          fontWeight: repoPDC?.[name] !== value && useConnection === "preDefined" ? "bold" : "normal",
         } }}
        className={"width-100"}
        name={name}
        autoComplete="off"
      />
    </FormControl>
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      onClick={() => {
        setValueLabel(t(label));
        setWorkflowSessionModal({ open: true, isFirstTime: true });
      }}
      sx={{ width: "40px", minWidth: "40px" }}>
      <MoreHorizOutlined fontSize="small" />
    </Button>
  </div>
});